import React from 'react'
import ProductPage from './ProductPage'

const ACCENT = '#d4af37'
const ACCENT_2 = '#f1d77a'
const URL = 'https://vmdproject.com/progetti/accademia-campania'

// Case study: solo dati → ProductPage. Tutto ciò che è scritto qui è già pubblico o documentato nel repo del progetto.
export const accademiaCampaniaData = {
  slug: 'accademia-campania',
  accent: ACCENT,
  accent2: ACCENT_2,

  seo: {
    title: 'Accademia Campania — Case study piattaforma danza sportiva',
    description: 'Come è stata costruita la piattaforma di Accademia Campania: sito pubblico, area riservata per direzione, tecnici e atleti, prenotazione di stage e gare. Next.js 16 e Supabase.',
    canonical: '/progetti/accademia-campania',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CreativeWork',
          '@id': `${URL}#casestudy`,
          name: 'Accademia Campania — piattaforma per la danza sportiva',
          description: 'Sito pubblico e area riservata a tre ruoli per la gestione di eventi, prenotazioni e atleti di un\'accademia di danza sportiva.',
          url: URL,
          inLanguage: 'it-IT',
          about: { '@type': 'WebSite', name: 'Accademia Campania', url: 'https://www.accademiacampania.com' },
          author: { '@id': 'https://vmdproject.com/#person' },
          publisher: { '@id': 'https://vmdproject.com/#org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vmdproject.com/' },
            { '@type': 'ListItem', position: 2, name: 'Progetti', item: 'https://vmdproject.com/progetti' },
            { '@type': 'ListItem', position: 3, name: 'Accademia Campania', item: URL },
          ],
        },
      ],
    },
  },

  eyebrow: '// case study',
  badge: 'Online da ottobre 2026',

  hero: {
    titleLead: 'Accademia Campania:',
    titleAccent: 'la danza sportiva, in una piattaforma.',
    intro: 'Sito pubblico e area riservata per un\'accademia di danza sportiva: la direzione pubblica stage e gare, le scuole affiliate iscrivono i propri atleti, ogni atleta ha il suo storico.',
    ctaTo: 'https://www.accademiacampania.com',
    ctaLabel: 'Visita il sito',
  },

  related: {
    to: '/collabora',
    label: 'Hai un progetto simile? Parliamone',
    footerLabel: 'Collabora',
  },

  stats: [
    { icon: '👥', label: 'Ruoli con permessi distinti', value: '3', color: ACCENT },
    { icon: '🌍', label: 'Lingue predisposte (IT, EN)', value: '2', color: '#22d3ee' },
    { icon: '🔒', label: 'Tabelle protette da policy RLS', value: '100%', color: '#a78bfa' },
  ],

  about: {
    eyebrow: '// il problema',
    title: 'Tre tipi di utenti, un solo posto.',
    text: [
      "L'Accademia Campania organizza stage, gare e formazione per la danza sportiva e lavora con una rete di scuole affiliate. Serviva una piattaforma unica, non un semplice sito vetrina: la direzione deve creare e pubblicare gli eventi, i tecnici delle scuole devono poter iscrivere i propri atleti, e ogni atleta deve ritrovare eventi, gare e il proprio diario.",
      "Il vincolo più delicato era la riservatezza: un tecnico vede solo i propri atleti, il listino è visibile solo alle strutture abilitate, e nessuno deve poter modificare importi o stato dei pagamenti dal proprio account.",
    ],
  },

  features: {
    eyebrow: '// la soluzione',
    title: 'Sito pubblico più area riservata.',
    lead: 'Un monorepo con una sola applicazione web: le pagine pubbliche raccontano l\'accademia, l\'area riservata cambia in base al ruolo di chi accede.',
    items: [
      {
        icon: '🏛️',
        title: 'Gerarchia Admin → Tecnico → Atleta',
        desc: 'La direzione approva le strutture. Il tecnico approvato invita i propri atleti con un link dedicato e li ritrova associati in automatico. Chi è in attesa di approvazione vede solo la pagina di attesa.',
      },
      {
        icon: '🎟️',
        title: 'Eventi e prenotazioni',
        desc: 'Stage e gare con pacchetti. Il tecnico seleziona più atleti insieme e vede il totale aggiornarsi in tempo reale; la direzione conferma i pagamenti e gestisce gli annullamenti.',
      },
      {
        icon: '🛡️',
        title: 'Regole applicate dal database',
        desc: 'La capienza non può scendere sotto i posti già prenotati, un evento con prenotazioni si annulla ma non si cancella, un evento senza pacchetti non si pubblica. Sono vincoli del database, non controlli del frontend.',
      },
      {
        icon: '🔐',
        title: 'Sicurezza verificata con test',
        desc: 'Ogni tabella ha policy Row Level Security per anonimo, admin, tecnico e atleta. Le policy sono coperte da test pgTAP, in maggioranza negativi: verificano ciò che un ruolo non deve riuscire a fare.',
      },
      {
        icon: '🎬',
        title: 'Hero che avanza con lo scroll',
        desc: 'Nella home il video della coppia avanza insieme allo scroll, con testo che si accende parola per parola e braci d\'oro disegnate su canvas. Su telefono le sezioni orizzontali diventano scroll nativo.',
      },
      {
        icon: '📓',
        title: 'Diario e storico dell\'atleta',
        desc: 'Ogni atleta ha lo storico di eventi e gare e un diario personale, visibile soltanto al proprio tecnico.',
      },
    ],
  },

  stack: ['Next.js 16', 'React 19', 'TypeScript', 'Tailwind 4', 'Supabase', 'pgTAP', 'GSAP', 'next-intl', 'Playwright', 'Brevo', 'Turborepo', 'Vercel'],

  faq: [
    { q: 'Di cosa si è occupato VMDProject?', a: 'Dell\'intero progetto: architettura, schema del database e policy di sicurezza, interfaccia, animazioni, email transazionali, ambienti di staging e produzione e messa online.' },
    { q: 'Perché la sicurezza è nel database e non nel frontend?', a: 'Perché un controllo fatto solo nell\'interfaccia si aggira chiamando direttamente le API. Con le policy Row Level Security è il database a rifiutare la richiesta, qualunque client la invii.' },
    { q: 'Com\'è organizzato il rilascio?', a: 'Due ambienti separati, staging e produzione, ciascuno con il proprio database. Ogni modifica passa da typecheck, lint, build e test del database prima di arrivare in produzione; i flussi principali sono coperti da test end-to-end.' },
  ],

  cta: {
    eyebrow: '// il tuo progetto',
    titleLead: 'Serve una piattaforma',
    titleAccent: 'come questa?',
    text: 'Aree riservate, ruoli, prenotazioni, pagamenti: raccontami cosa ti serve e valutiamo insieme come costruirlo.',
    to: '/collabora',
    label: 'Parliamone',
  },
}

export default function AccademiaCampania() {
  return <ProductPage data={accademiaCampaniaData} />
}
